import type { Translations } from '@/i18n/types';

const de: Translations = {
  /* ── Common ───────────────────────────────────────────────── */
  'common.add': 'Hinzufügen',
  'common.save': 'Speichern',
  'common.create': 'Erstellen',
  'common.edit': 'Bearbeiten',
  'common.delete': 'Löschen',
  'common.cancel': 'Abbrechen',
  'common.close': 'Schliessen',
  'common.back': 'Zurück',
  'common.skip': 'Überspringen',
  'common.search': 'Suchen',
  'common.retry': 'Erneut versuchen',
  'common.reloadPage': 'Seite neu laden',
  'offline.title': 'Du bist offline',
  'offline.message': 'Du hast derzeit keine Internetverbindung.',
  'common.loading': 'Laden…',
  'common.optional': '(optional)',
  'common.yes': 'Ja',
  'common.no': 'Nein',
  'common.none': '— Keine —',
  'common.notSet': '— Nicht gesetzt —',
  'common.deleting': 'Löschen…',
  'common.applying': 'Anwenden…',
  'common.closeDialog': 'Dialog schliessen',
  'common.prev': 'Zurück',
  'common.next': 'Weiter',

  /* ── Filter UI ────────────────────────────────────────────── */
  'filter.add': 'Filter hinzufügen',
  'filter.allActive': 'Alle Filter aktiv',
  'filter.clearAll': 'Alle zurücksetzen',
  'filter.clear': 'Leeren',
  'filter.apply': 'Anwenden',
  'filter.cancel': 'Abbrechen',
  'filter.condition': 'Bedingung',
  'filter.presets': 'Vorlagen',
  'filter.customRange': 'Benutzerdefinierter Bereich',
  'filter.from': 'Von',
  'filter.to': 'Bis',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Wert',
  'filter.searchOptions': 'Optionen suchen...',
  'filter.noOptions': 'Keine Optionen gefunden',
  'filter.loadingOptions': 'Optionen werden geladen...',

  /* ── Auth ─────────────────────────────────────────────────── */
  'auth.signIn': 'Anmelden',
  'auth.signInWithGoogle': 'Mit Google anmelden',
  'auth.signInWithApple': 'Mit Apple anmelden',
  'auth.or': 'oder',
  'auth.signOut': 'Abmelden',
  'auth.welcome': 'Willkommen',
  'auth.subtitle': 'Kart Editor',
  'auth.signingIn': 'Wird angemeldet...',

  /* ── Settings ───────────────────────────────────────────────── */
  'settings.title': 'Einstellungen',
  'settings.account': 'Konto',
  'settings.profile': 'Profil',
  'settings.displayName': 'Anzeigename',
  'settings.saveName': 'Name speichern',
  'settings.nameSaved': 'Name erfolgreich aktualisiert',
  'settings.linkedAccounts': 'Verknüpfte Konten',
  'settings.linkGoogle': 'Google-Konto verknüpfen',
  'settings.linkApple': 'Apple-Konto verknüpfen',
  'settings.unlink': 'Trennen',
  'settings.linked': 'Verknüpft',
  'settings.notLinked': 'Nicht verknüpft',
  'settings.cannotUnlinkOnlyProvider': 'Mindestens ein Anmeldedienst muss verknüpft bleiben.',
  'settings.language': 'Sprache',
  'settings.tracks': 'Strecken',
  'settings.categories': 'Kategorien',
  'settings.drivers': 'Fahrer',
  'settings.userId': 'Benutzer-ID',
  'settings.signOut': 'Abmelden',
  'settings.connectedApps': 'Verbundene Anwendungen',
  'settings.connectedAppsDesc': 'Verwalte KI-Assistenten, IDEs und Tools, die über MCP auf deine Telemetriedaten zugreifen dürfen.',
  'settings.noConnectedApps': 'Noch keine verbundenen Anwendungen. Autorisiere einen MCP-Client (wie Claude Desktop oder Cursor), um ihn hier zu sehen.',
  'settings.authorizedOn': 'Autorisiert am',
  'settings.lastUsed': 'Zuletzt verwendet',
  'settings.neverUsed': 'Nie',
  'settings.revoke': 'Widerrufen',
  'settings.revokeAppConfirm': 'Möchtest du dieser Anwendung den Zugriff wirklich entziehen? Alle aktiven Verbindungen werden sofort ungültig.',
  'settings.appRevokedSuccess': 'Anwendungszugriff erfolgreich widerrufen',
  'settings.copyKey': 'Code kopieren',
  'settings.keyCopied': 'In Zwischenablage kopiert',
  'settings.mcpConfigSnippet': 'MCP-Client-Konfiguration',
  'settings.mcpConfigDesc': 'Füge diese Konfiguration zu deiner Claude Desktop (claude_desktop_config.json) oder Cursor MCP-Konfiguration hinzu. Die Autorisierung erfolgt automatisch über deinen Browser:',
  'settings.deleteAccount': 'Konto löschen',
  'settings.deleteAccountDesc': 'Lösche dein Konto sowie alle zugehörigen Sessions, Telemetriedaten, gespeicherten Analysen, Ausrüstung, Kart-Setups und Freigaben dauerhaft.',
  'settings.deleteAccountConfirmTitle': 'Konto dauerhaft löschen?',
  'settings.deleteAccountConfirmDesc': 'Diese Aktion kann nicht rückgängig gemacht werden. Alle deine eigenen Sessions, Telemetriedaten, gespeicherten Analysen, Ausrüstungsgegenstände, Kart-Setups, Teams und geteilten Session-Einträge werden sofort und unwiderruflich gelöscht.',
  'settings.deleteAccountConfirmPrompt': 'Gib zur Bestätigung „{phrase}“ in das Feld unten ein:',
  'settings.deleteAccountConfirmPhrase': 'mein konto löschen',
  'settings.deleteAccountConfirmButton': 'Mein Konto löschen',

  /* ── Common fields ────────────────────────────────────────── */
  'common.brand': 'Marke',
  'common.type': 'Typ',
  'common.nr': 'Nr#',
  'common.nrInfo': 'Fortlaufende Nummer zur Identifikation dieses Eintrags.',
  'common.year': 'Jahr',
  'common.stiffness': 'Härte',
  'common.width': 'Breite',
  'common.position': 'Position',

  /* ── Tabs ──────────────────────────────────────────────────── */
  'tab.engines': 'Motoren',
  'tab.tyres': 'Reifen',
  'tab.chassis': 'Chassis',
  'tab.carburetors': 'Vergaser',
  'tab.axles': 'Achsen',
  'tab.hubs': 'Radnaben',
  'tab.spark_plugs': 'Zündkerzen',
  'tab.tracks': 'Strecken',
  'tab.categories': 'Kategorien',
  'tab.drivers': 'Fahrer',
  'tab.races': 'Rennen',
  'tab.users': 'Benutzer',
  'tab.settings': 'Einstellungen',

  /* ── Subtabs ──────────────────────────────────────────────── */

  /* ── Navigation ────────────────────────────────────────────── */
  'nav.administration': 'Administration',

  /* ── CRUD ──────────────────────────────────────────────────── */
  'crud.requiredField': '{field} ist ein Pflichtfeld',
  'crud.updated': '{entity} aktualisiert',
  'crud.created': '{entity} erstellt',
  'crud.deleted': '{entity} gelöscht',
  'crud.confirmDelete': '{entity} „{name}" löschen?\nDies kann nicht rückgängig gemacht werden.',
  'crud.emptyState': 'Noch keine {title}. Füge oben erstmals einen {entity} hinzu.',
  'crud.editEntity': '{entity} bearbeiten',
  'crud.newEntity': 'Neuer {entity}',

  /* ── Entities ──────────────────────────────────────────────── */
  'entity.engine': 'Motor',
  'entity.tyre': 'Reifen',
  'entity.chassis': 'Chassis',
  'entity.carburetor': 'Vergaser',
  'entity.axle': 'Achse',
  'entity.hub': 'Radnabe',
  'entity.spark_plug': 'Zündkerze',
  'entity.track': 'Strecke',
  'entity.category': 'Kategorie',
  'entity.driver': 'Fahrer',

  /* ── Sessions ──────────────────────────────────────────────── */

  /* ── Session Setup Dialog ──────────────────────────────────── */

  /* ── Search Help Dialog ────────────────────────────────────── */

  /* ── Import Dialog ─────────────────────────────────────────── */
  'import.title': 'Renn-PDFs importieren',
  'import.uploadDesc': 'Lade RMC-Rennergebnis-PDFs hoch. Sie werden geparst und zur Überprüfung angezeigt, bevor sie importiert werden.',
  'import.dragDropPdf': 'PDF-Dateien hierher ziehen',
  'import.orBrowse': 'oder',
  'import.browseFiles': 'Dateien durchsuchen',
  'import.onlyPdfAccepted': 'Nur PDF-Dateien werden akzeptiert.',
  'import.filesSelected': '{n} Datei(en) ausgewählt',
  'import.upload': 'Hochladen & Parsen',
  'import.uploadingPct': 'Hochladen… {n}%',
  'import.parseFailed': 'Antwort konnte nicht geparst werden.',
  'import.uploadFailed': 'Upload fehlgeschlagen. Bitte erneut versuchen.',
  'import.connectionLost': 'Verbindung verloren.',
  'import.uploadAborted': 'Upload abgebrochen.',
  'import.reviewDesc': 'Geparste Sessions überprüfen. Abwählen, was nicht importiert werden soll.',
  'import.selectAll': 'Alle auswählen',
  'import.deselectAll': 'Alle abwählen',
  'import.existsInDb': 'Vorhanden — wird überschrieben',
  'import.entriesCount': '{n} Einträge',
  'import.submit': 'Importieren',
  'import.submitting': 'Sessions werden importiert…',
  'import.importedCount': '{n} Session(s) importiert.',
  'import.successMessage': 'Sessions erfolgreich importiert!',
  'import.noNewSessions': 'Keine neuen Sessions importiert.',
  'import.imported': 'Importiert',
  'import.skipped': 'Übersprungen',
  'import.errors': 'Fehler',
  'import.importButton': 'PDFs importieren',

  /* ── Kart Setup Dialog ─────────────────────────────────────── */

  /* ── Setup Tab ──────────────────────────────────────────────── */

  /* ── Tyre Pressures ────────────────────────────────────────── */

  /* ── Front Geometry ────────────────────────────────────────── */

  /* ── Rear Config ───────────────────────────────────────────── */

  /* ── Engine & Gear ──────────────────────────────────────────── */

  /* ── Carburetor Tuning ──────────────────────────────────────── */

  /* ── Chassis Select ─────────────────────────────────────────── */

  /* ── Notes ──────────────────────────────────────────────────── */

  /* ── Propagation Wizard ─────────────────────────────────────── */

  /* ── Propagation field labels ───────────────────────────────── */

  /* ── Tracks ─────────────────────────────────────────────────── */
  'tracks.name': 'Streckenname',
  'tracks.latitude': 'Breitengrad',
  'tracks.longitude': 'Längengrad',
  'tracks.searchAddress': 'Adresse suchen…',
  'tracks.coordinates': 'Koordinaten',
  'tracks.clickToAdjust': 'Klicke auf die Karte, um die Markierung anzupassen',
  'tracks.tabLocation': 'Standort',
  'tracks.tabAliases': 'Verknüpfungen',
  'tracks.sessionTracks': 'Session-Strecken',
  'tracks.raceEventTracks': 'Renn-Strecken',
  'tracks.noSessionTracks': 'Keine Session-Strecken verfügbar',
  'tracks.noRaceEventTracks': 'Keine Renn-Strecken verfügbar',
  'tracks.linkedAliases': '{sessions} Session-Strecken · {races} Renn-Strecken',
  'tracks.satelliteImage': 'Satellitenbild',
  'tracks.fetchImage': 'Satellitenbild laden',
  'tracks.refetchImage': 'Satellitenbild neu laden',
  'tracks.fetchingImage': 'Bild wird geladen…',
  'tracks.imageFetched': 'Satellitenbild erfolgreich geladen',
  'tracks.imageFetchFailed': 'Fehler beim Laden des Satellitenbilds',
  'tracks.noImage': 'Kein Satellitenbild',
  'tracks.imageZoom': 'Zoomstufe',
  'tracks.autoZoom': 'Automatischer Zoom',

  /* ── Categories ─────────────────────────────────────────────── */
  'categories.name': 'Name',
  'categories.aliases': 'Kategorie-Aliase',
  'categories.noAliases': 'Keine Kategorienamen verfügbar',
  'categories.linkedAliases': '{count} Aliase verknüpft',

  /* ── Races ──────────────────────────────────────────────────── */
  'races.title': 'Rennsessions',
  'races.loading': 'Rennsessions laden…',
  'races.empty': 'Noch keine Rennsessions. Importiere Renn-PDFs, um zu beginnen.',
  'races.loadFailed': '⚠ Fehler beim Laden der Rennsessions: {error}',
  'races.date': 'Datum',
  'races.track': 'Strecke',
  'races.sessionType': 'Typ',
  'races.category': 'Kategorie',
  'races.entries': 'Teilnehmer',
  'races.laps': 'Runden',
  'races.championship': 'Meisterschaft',
  'races.deleteConfirmTitle': 'Rennsession löschen',
  'races.deleteConfirmMessage': 'Möchtest du die <strong>{type}</strong>-Session auf <strong>{track}</strong> am <strong>{date}</strong> wirklich löschen? Alle Einträge und Rundendaten werden dauerhaft entfernt.',
  'races.deleteConfirmButton': 'Session löschen',
  'races.deleted': 'Rennsession gelöscht',
  'races.detailTitle': 'Rennsession',
  'races.detailSubtitle': '{type} · {track} · {date}',
  'races.position': 'Pos',
  'races.kart': 'Kart',
  'races.driver': 'Fahrer',
  'races.firstName': 'Vorname',
  'races.lastName': 'Nachname',
  'races.nationality': 'Nat',
  'races.grid': 'Startplatz',
  'races.bestLap': 'Beste Runde',
  'races.totalTime': 'Gesamtzeit',
  'races.penalty': 'Strafe',
  'races.gap': 'Abstand',
  'races.status': 'Status',
  'races.deleteEntry': 'Eintrag löschen',
  'races.entryDeleted': 'Eintrag gelöscht',
  'races.saved': 'Rennsession gespeichert',
  'races.searchPlaceholder': 'Rennen suchen… z.B. Castelletto oder type:race',
  'races.querySyntaxHelp': 'Hilfe zur Suchsyntax',
  'races.sessionCount': '{count} Sessions',
  'races.sessionCountSingular': '1 Session',

  /* ── Race Search Help Dialog ──────────────────────────────── */
  'raceSearchHelp.title': 'Rennsuchsyntax',
  'raceSearchHelp.subtitle': 'Filtere Rennsessions mit Feld:Wert-Paaren. Mehrere Begriffe werden mit UND verknüpft.',
  'raceSearchHelp.bareText': '(Freitext)',
  'raceSearchHelp.bareTextDesc': 'Sucht im Streckennamen oder Meisterschaftsnamen nach dem Text.',
  'raceSearchHelp.track': 'track',
  'raceSearchHelp.trackDesc': 'Sucht im Streckennamen nach dem Wert.',
  'raceSearchHelp.championship': 'championship',
  'raceSearchHelp.championshipDesc': 'Sucht im Meisterschaftsnamen nach dem Wert.',
  'raceSearchHelp.category': 'category',
  'raceSearchHelp.categoryDesc': 'Sucht im Kategorienamen nach dem Wert.',
  'raceSearchHelp.type': 'type',
  'raceSearchHelp.typeDesc': 'Sucht nach Sessiontyp oder -bezeichnung (z.B. race, qualifying, heat).',
  'raceSearchHelp.entries': 'entries',
  'raceSearchHelp.entriesDesc': 'Filtert nach Teilnehmeranzahl. Unterstützt =, >, >=, <, <= und Bereiche.',
  'raceSearchHelp.laps': 'laps',
  'raceSearchHelp.lapsDesc': 'Filtert nach Rundenanzahl. Unterstützt Vergleichsoperatoren und Bereiche.',
  'raceSearchHelp.season': 'season',
  'raceSearchHelp.seasonDesc': 'Filtert nach Meisterschafts-Saisonjahr. Unterstützt Vergleichsoperatoren.',
  'raceSearchHelp.from': 'from',
  'raceSearchHelp.fromDesc': 'Rennen ab einem Datum. Akzeptiert JJJJ-MM-TT oder relativ: -7d, -2w, -3m, -1y.',
  'raceSearchHelp.to': 'to',
  'raceSearchHelp.toDesc': 'Rennen bis zu einem Datum. Gleiche Formate wie „from".',
  'raceSearchHelp.operatorsTitle': 'Operatoren & Logik',
  'raceSearchHelp.numericComparisons': 'Numerische Vergleiche:',
  'raceSearchHelp.range': 'Bereich:',
  'raceSearchHelp.rangeDesc': '— trifft auf Werte zwischen 5 und 20 zu (inklusive).',
  'raceSearchHelp.booleanLogic': 'Boolesche Logik:',
  'raceSearchHelp.booleanLogicDesc': 'Begriffe werden implizit mit',
  'raceSearchHelp.quotes': 'Anführungszeichen:',
  'raceSearchHelp.quotesDesc': 'Werte mit Leerzeichen in doppelte Anführungszeichen setzen, z.B.',
  'raceSearchHelp.examplesTitle': 'Beispielabfragen',
  'raceSearchHelp.example1Desc': 'Rennsessions in Castelletto',
  'raceSearchHelp.example2Desc': 'RMC-Meisterschaftsrennen aus dem letzten Jahr',
  'raceSearchHelp.example3Desc': 'Rennen mit 20+ Teilnehmern und 5+ Runden',

  /* ── Pagination ─────────────────────────────────────────────── */
  'pagination.eventsFound': '{n} Event(s) gefunden',

  /* ── Tyre IDs ──────────────────────────────────────────────── */

  /* ── Settings ──────────────────────────────────────────────── */
  'settings.users': 'Benutzer',

  /* ── Drivers ──────────────────────────────────────────────── */
  'drivers.canonicalDriver': 'Kanonischer Fahrer',
  'drivers.aliases': 'Aliase',
  'drivers.aliasesHeader': 'Aliase',
  'drivers.raceDrivers': 'Rennfahrer',
  'drivers.sessionDrivers': 'Session-Fahrer',
  'drivers.noRaceDrivers': 'Keine Rennfahrer verfügbar',
  'drivers.noSessionDrivers': 'Keine Session-Fahrer verfügbar',
  'drivers.selectDriver': 'Fahrer auswählen…',
  'drivers.searchDrivers': 'Fahrer suchen…',
  'drivers.createTitle': 'Neuer Fahrer-Alias',
  'drivers.editTitle': 'Fahrer-Alias bearbeiten',
  'drivers.stepCanonical': 'Fahrer wählen',
  'drivers.stepAliases': 'Aliase wählen',

  /* ── Sharing ─────────────────────────────────────────────── */
  
  /* ── Users ──────────────────────────────────────────────── */
  'users.title': 'Benutzer',
  'users.empty': 'Keine Benutzer gefunden',
  'users.loading': 'Benutzer werden geladen...',
  'users.searchPlaceholder': 'Benutzer suchen...',
  'users.name': 'Name',
  'users.email': 'E-Mail',
  'users.user': 'Benutzer',
  'users.admin': 'Admin',
  'users.owner': 'Eigentümer',
  'users.promote': 'Befördern',
  'users.demote': 'Herabstufen',
  'users.promoted': '{name} zum Admin befördert',
  'users.demoted': '{name} als Admin entfernt',
  'users.cannotDemoteOwner': 'Der Eigentümer kann nicht herabgestuft werden',
  'users.cannotDemoteSelf': 'Du kannst dich nicht selbst herabstufen',
  'users.confirmPromote': '{name} zum Administrator befördern? Die Person erhält vollen Zugriff auf den Editor.',
  'users.confirmDemote': 'Administratorzugriff von {name} entfernen? Die Person kann den Editor nicht mehr verwenden.',
  'users.accessDenied': 'Zugriff verweigert',
  'users.accessDeniedMessage': 'Du hast keine Administratorrechte. Kontaktiere einen Administrator, um Zugriff anzufordern.',
  'users.joinedAt': 'Beigetreten',
  'users.lastLogin': 'Letzter Login',
  'users.count': '{count} Benutzer',
  'users.countSingular': '1 Benutzer',
  
  /* ── User Search Help Dialog ────────────────────────────── */
  'userSearchHelp.title': 'Benutzersuche',
  'userSearchHelp.subtitle': 'Filtere die Benutzerliste mit feldspezifischen oder Freitextabfragen.',
  'userSearchHelp.bareText': 'Freitext',
  'userSearchHelp.bareTextDesc': 'Sucht in Name und E-Mail-Adresse.',
  'userSearchHelp.name': 'name:',
  'userSearchHelp.nameDesc': 'Nach Anzeigename filtern.',
  'userSearchHelp.email': 'email:',
  'userSearchHelp.emailDesc': 'Nach E-Mail-Adresse filtern.',
  'userSearchHelp.login': 'login:',
  'userSearchHelp.loginDesc': 'Nach letztem Login filtern. Einzelwerte (-7d) oder Bereiche (-30d..today). Relative (-7d, -2w, -3m), absolute Daten (2026-01-15) oder today.',
  'userSearchHelp.created': 'created:',
  'userSearchHelp.createdDesc': 'Nach Erstellungsdatum filtern. Einzelwerte (-30d) oder Bereiche (-1y..today). Relative (-7d, -2w, -3m), absolute Daten (2026-01-15) oder today.',
  'userSearchHelp.examplesTitle': 'Beispiele',
  'userSearchHelp.example1Desc': 'Benutzer namens Yanick finden',
  'userSearchHelp.example2Desc': 'Benutzer mit Gmail-Adressen finden',
  'userSearchHelp.example3Desc': 'Benutzer finden, die sich in den letzten 7 Tagen angemeldet haben',
  'userSearchHelp.example4Desc': 'Benutzer finden, die in einem Datumsbereich erstellt wurden',

  /* ── Bugs tab ───────────────────────────────────────────────── */
  'tab.bugs': 'Fehler',
  'bugs.title': 'Fehlerberichte',
  'bugs.empty': 'Keine Fehlerberichte gefunden.',
  'bugs.loading': 'Fehlerberichte werden geladen…',
  'bugs.searchPlaceholder': 'Fehlerberichte suchen…',
  'bugs.reporter': 'Melder',
  'bugs.description': 'Beschreibung',
  'bugs.pageUrl': 'Seiten-URL',
  'bugs.status': 'Status',
  'bugs.statusOpen': 'Offen',
  'bugs.statusResolved': 'Gelöst',
  'bugs.createdAt': 'Erstellt',
  'bugs.resolvedAt': 'Gelöst am',
  'bugs.resolve': 'Lösen',
  'bugs.reopen': 'Wiedereröffnen',
  'bugs.resolved': 'Fehlerbericht gelöst.',
  'bugs.reopened': 'Fehlerbericht wiedereröffnet.',
  'bugs.count': '{count} Berichte',
  'bugs.countSingular': '1 Bericht',
  'bugs.detailTitle': 'Fehlerbericht',
  'bugs.userAgent': 'User Agent',
  'bugs.screenSize': 'Bildschirmgrösse',
  'bugs.appLanguage': 'Sprache',
  'bugs.copy': 'Text kopieren',
  'bugs.copied': 'In die Zwischenablage kopiert',

  /* ── Bug search help ────────────────────────────────────────── */
  'bugSearchHelp.title': 'Suchsyntax',
  'bugSearchHelp.subtitle': 'Verwende diese Präfixe, um die Suche einzugrenzen.',
  'bugSearchHelp.bareText': 'Freitext',
  'bugSearchHelp.bareTextDesc': 'Sucht in Beschreibung und Meldername.',
  'bugSearchHelp.status': 'status:open',
  'bugSearchHelp.statusDesc': 'Nach Status filtern (open oder resolved).',
  'bugSearchHelp.email': 'email:Wert',
  'bugSearchHelp.emailDesc': 'Nach Melder-E-Mail filtern.',
  'bugSearchHelp.examplesTitle': 'Beispiele',
  'bugSearchHelp.example1Desc': 'Alle offenen Fehlerberichte finden.',
  'bugSearchHelp.example2Desc': 'Fehlerberichte von Gmail-Nutzern finden.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'Ein Fehler ist aufgetreten und wurde automatisch gemeldet.',
  'errorBoundary.title': 'Etwas ist schiefgelaufen',
  'errorBoundary.message': 'Ein unerwarteter Fehler ist aufgetreten und wurde an den Server gemeldet.',
  'errorBoundary.reload': 'Seite neu laden',
  'errorBoundary.reset': 'Erneut versuchen',
  'errorBoundary.showDetails': 'Fehlerdetails anzeigen',
  'errorBoundary.hideDetails': 'Fehlerdetails ausblenden',

  /* ── Profiles ───────────────────────────────────────────────── */
  'tab.profiles': 'Profile',
  'profiles.engineProfiles': 'Motorenprofile',
  'profiles.tyreProfiles': 'Reifenprofile',
  'profiles.addEngineProfile': 'Motorenprofil hinzufügen',
  'profiles.editEngineProfile': 'Motorenprofil bearbeiten',
  'profiles.addTyreProfile': 'Reifenprofil hinzufügen',
  'profiles.editTyreProfile': 'Reifenprofil bearbeiten',
  'profiles.brand': 'Marke',
  'profiles.type': 'Typ',
  'profiles.compound': 'Mischung',
  'profiles.compoundDry': 'Trocken',
  'profiles.compoundWet': 'Nass',
  'profiles.deleteBlocked': 'Kann nicht gelöscht werden — wird noch von Material verwendet.',
  'profiles.emptyEngineProfiles': 'Noch keine Motorenprofile.',
  'profiles.emptyTyreProfiles': 'Noch keine Reifenprofile.',
  'entity.engineProfile': 'Motorenprofil',
  'entity.tyreProfile': 'Reifenprofil',

  /* ── Track Shape Studio ────────────────────────────────────── */
  'trackStudio.title': 'Streckenform- & Annotations-Studio',
  'trackStudio.subtitle': 'Streckengeometrie entwerfen, Splines bearbeiten und Kurven-/Sektor-Markierungen platzieren',
  'trackStudio.openStudio': 'Streckengeometrie & Annotationen',
  'trackStudio.modeSpline': 'Geometrie & Splines',
  'trackStudio.modeCorners': 'Kurven',
  'trackStudio.modeSectors': 'Sektoren',
  'trackStudio.modeSegments': 'Segmente',
  'trackStudio.bakeSegments': '200 Segmente generieren',
  'trackStudio.rebakeSegments': '200 Segmente neu generieren',
  'trackStudio.segmentsBakedSuccess': '{count} Streckensegmente erfolgreich generiert',
  'trackStudio.segmentsCount': '{count} Segmente',
  'trackStudio.segmentsStatusBaked': '200 Segmente generiert',
  'trackStudio.segmentsStatusNotBaked': 'Noch nicht generiert',
  'trackStudio.segmentsHint': 'Teilt die Streckenmittellinie in 200 äquidistante statische GPS-Segmente ab der Start-/Ziellinie für hochpräzise Rundenvergleiche.',
  'trackStudio.segmentDetails': 'Segment-Details',
  'trackStudio.segmentIndex': 'Segment #{index}',
  'trackStudio.avgSegmentLength': 'Durchschn. Segmentlänge',
  'trackStudio.searchSegments': 'Segmente suchen...',
  'trackStudio.modeAnnotations': 'Kurven- & Sektor-Annotationen',
  'trackStudio.cornersCount': 'Streckenkurven ({count})',
  'trackStudio.sectorsCount': 'Timing-Sektoren ({count})',
  'trackStudio.clickCornersHint': 'Klicke auf die Strecke, um eine Kurve hinzuzufügen. Ziehe den Apex oder passe Einlenk-/Ausgangspunkte an.',
  'trackStudio.clickSectorsHint': 'Klicke auf die Strecke, um eine Sektormarkierung zu setzen. Ziehe Markierungen zum Verschieben entlang der Strecke.',
  'trackStudio.trackLength': 'Streckenlänge: {length}m',
  'trackStudio.trackWidth': 'Streckenbreite',
  'trackStudio.defaultTrackWidth': 'Standard-Streckenbreite',
  'trackStudio.pointWidth': 'Punkt-Breite',
  'trackStudio.customWidth': 'Eigene Breite für diesen Punkt',
  'trackStudio.useDefaultWidth': 'Standard verwenden ({width}m)',
  'trackStudio.resetPointWidth': 'Auf Standard zurücksetzen',
  'trackStudio.segmentWidth': 'Segment-Breiten-Werkzeug',
  'trackStudio.segmentWidthDesc': 'Breite anpassen oder fließende Breitenübergänge zwischen Punkten erstellen',
  'trackStudio.fromPoint': 'Von Punkt',
  'trackStudio.toPoint': 'Bis Punkt',
  'trackStudio.startWidth': 'Start-Breite',
  'trackStudio.endWidth': 'End-Breite',
  'trackStudio.transitionMode': 'Übergangsart',
  'trackStudio.modeConstant': 'Konstante Breite',
  'trackStudio.modeGradient': 'Fließender Übergang (Verlauf)',
  'trackStudio.applySegment': 'Breite auf Segment anwenden',
  'trackStudio.resetSegment': 'Segmentbreite zurücksetzen',
  'trackStudio.segmentApplied': 'Breite für Punkte {from} bis {to} aktualisiert',
  'trackStudio.controlPoints': 'Mittellinien-Kontrollpunkte ({count})',
  'trackStudio.addPointHint': 'Auf die Kurve oder Leinwand klicken, um Punkte einzufügen. Punkte ziehen zum Verformen.',
  'trackStudio.selectedPoint': 'Ausgewählter Punkt #{index}',
  'trackStudio.deletePoint': 'Punkt löschen',
  'trackStudio.evenSpacePoints': 'Punkte gleichmäßig verteilen',
  'trackStudio.reverseDirection': 'Streckenrichtung umkehren',
  'trackStudio.layerSatellite': 'Satellitenbild',
  'trackStudio.layerCenterline': 'Mittellinien-Spline',
  'trackStudio.layerBoundaries': 'Streckenbegrenzung',
  'trackStudio.layerAnnotations': 'Annotationen & Markierungen',
  'trackStudio.undo': 'Rückgängig (Cmd+Z)',
  'trackStudio.redo': 'Wiederholen (Cmd+Shift+Z)',
  'trackStudio.reset': 'Änderungen zurücksetzen',
  'trackStudio.saveSuccess': 'Streckenform und Annotationen erfolgreich gespeichert!',
  'trackStudio.autoSaveSuccess': 'Streckenform und Annotationen automatisch gespeichert',
  'trackStudio.saveError': 'Speichern der Streckenform fehlgeschlagen: {error}',
  'trackStudio.noCenterline': 'Auf das Satellitenbild klicken, um den ersten Kontrollpunkt für die Streckenmittellinie zu erstellen.',
  'trackStudio.annotations': 'Streckenannotationen ({count})',
  'trackStudio.addCorner': 'Kurve hinzufügen (K{next})',
  'trackStudio.addSector': 'Sektormarkierung hinzufügen',
  'trackStudio.addFinishLine': 'Start-/Ziellinie festlegen',
  'trackStudio.cornerName': 'Kurvenname',
  'trackStudio.cornerDirection': 'Kurvenrichtung',
  'trackStudio.directionLeft': 'Linkskurve',
  'trackStudio.directionRight': 'Rechtskurve',
  'trackStudio.entry': 'Einlenkpunkt (Anbremsen)',
  'trackStudio.apex': 'Scheitelpunkt (Apex)',
  'trackStudio.exit': 'Ausgangspunkt (Herausbeschleunigen)',
  'trackStudio.deleteAnnotation': 'Annotation löschen',
  'trackStudio.noAnnotations': 'Noch keine Annotationen erstellt. Klicke auf die Strecke, um Kurven, Sektoren oder die Start-/Ziellinie zu platzieren.',
  'trackStudio.finishLinePlaced': 'Start-/Ziellinie ist aktiv bei {pos}% der Runde.',
  'trackStudio.closeConfirm': 'Es gibt ungespeicherte Änderungen. Möchtest du das Studio wirklich schließen?',
  'trackStudio.menuAddCorner': 'Kurve hinzufügen',
  'trackStudio.menuAddSector': 'Sektormarkierung hinzufügen',
  'trackStudio.menuSetFinishLine': 'Start-/Ziellinie hier setzen',
  'trackStudio.menuTrackPosition': 'Streckenposition',
  'trackStudio.clickTrackHint': 'Klicke auf die Strecke, um eine Kurve oder Sektormarkierung hinzuzufügen oder die Start-/Ziellinie zu setzen.',
  'trackStudio.drivingDirection': 'Fahrtrichtung',
  'trackStudio.reverseDrivingDirection': 'Fahrtrichtung umkehren',
  'trackStudio.drivingDirectionDesc': 'Kehrt die Fahrtrichtung der Strecke um. Alle Kurvenreihenfolgen und Sektoren passen sich automatisch an.',
  'trackStudio.multiplePointsSelected': '{count} Punkte ausgewählt',
  'trackStudio.selectAll': 'Alle auswählen (Cmd+A)',
  'trackStudio.deselectAll': 'Auswahl aufheben (Esc)',
  'trackStudio.deleteSelectedPoints': '{count} Punkte löschen',
  'trackStudio.customWidthSelection': 'Benutzerdefinierte Breite (Ausgewählte Punkte)',
  'tracks.reanalyzeLaps': 'Streckenrunden neu analysieren',
  'tracks.reanalyzeAllLaps': 'Alle Runden neu analysieren',
  'tracks.reanalyzeQueued': '{enqueued} Runde(n) zur Analyse eingereiht ({skipped} bereits aktuell).',
  'tracks.reanalyzeForceQueued': '{enqueued} Runde(n) zur vollständigen Neuberechnung eingereiht.',
  'tracks.reanalyzeAllUpToDate': 'Alle {skipped} Runde(n) sind bereits auf dem neuesten Streckenstand.',
  'tracks.reanalyzeConfirmTitle': 'Streckenrunden neu analysieren',
  'tracks.reanalyzeConfirmMessage': 'Der Streckendaten-Hash für „{name}" hat sich nicht geändert ({count} Runde(n) sind bereits aktuell). Möchtest du trotzdem eine vollständige Neuberechnung für diese Strecke erzwingen?',
  'tracks.reanalyzeConfirmButton': 'Runden neu analysieren',
  'tracks.reanalyzeAllConfirmTitle': 'Alle Runden neu analysieren',
  'tracks.reanalyzeAllConfirmMessage': 'Möchtest du wirklich eine vollständige Neuberechnung aller Runden und Sessions über alle Strecken hinweg starten?',

  /* ── Monitoring ────────────────────────────────────────────── */
  'tab.monitoring': 'Monitoring',
  'monitoring.title': 'Warteschlangen-Monitoring',
  'monitoring.subtitle': 'Echtzeit-Statistiken und Steuerung für die Runden- und Session-Analyse-Warteschlange',
  'monitoring.refresh': 'Aktualisieren',
  'monitoring.autoRefreshActive': 'Automatische Aktualisierung alle 3s während aktiv',
  'monitoring.lapQueueTitle': 'Runden-Analyse-Warteschlange',
  'monitoring.sessionQueueTitle': 'Session-Analyse-Warteschlange',
  'monitoring.pending': 'Ausstehend',
  'monitoring.processing': 'In Bearbeitung',
  'monitoring.completed': 'Abgeschlossen',
  'monitoring.failed': 'Fehlgeschlagen',
  'monitoring.totalEligible': 'Gesamt berechtigt',
  'monitoring.actionsTitle': 'Globale Warteschlangen-Steuerung',
  'monitoring.syncUnanalyzed': 'Nicht analysierte & veraltete synchronisieren',
  'monitoring.syncUnanalyzedDesc': 'Alle Strecken prüfen und fehlende oder veraltete Rundenanalysen einreihen',
  'monitoring.syncUnanalyzedDone': '{enqueued} Runde(n) eingereiht ({skipped} bereits aktuell).',
  'monitoring.retryFailed': 'Fehlgeschlagene Jobs wiederholen',
  'monitoring.retryFailedDesc': 'Alle fehlgeschlagenen Runden- und Session-Jobs zurück auf ausstehend setzen',
  'monitoring.retryFailedDone': '{retried} fehlgeschlagene(n) Job(s) zurückgesetzt.',
  'monitoring.reanalyzeAll': 'Alle Runden komplett neu analysieren',
  'monitoring.reanalyzeAllDesc': 'Vollständige Neuberechnung aller Runden und Sessions über alle Strecken erzwingen',
  'monitoring.userQueuesTitle': 'Fortschritt pro Benutzer (Round-Robin)',
  'monitoring.userQueuesSubtitle': 'Worker verarbeitet Jobs im Round-Robin-Verfahren pro Benutzer, damit grosse Batches andere Benutzer nicht blockieren',
  'monitoring.userQueuesEmpty': 'Keine aktiven oder eingereihten Jobs pro Benutzer.',
  'monitoring.user': 'Benutzer',
  'monitoring.systemQueue': 'System / Global',
  'monitoring.lapsStatus': 'Runden-Jobs',
  'monitoring.sessionsStatus': 'Session-Jobs',
  'monitoring.progress': 'Fortschritt',
  'monitoring.tracksTitle': 'Strecken-Analyseabdeckung & Steuerung',
  'monitoring.tracksSubtitle': 'Analyseabdeckung pro Strecke prüfen und inkrementelle oder vollständige Neuberechnung starten',
  'monitoring.tracksEmpty': 'Noch keine kanonischen Strecken konfiguriert.',
  'monitoring.trackCoverage': 'Abdeckung',
  'monitoring.upToDateLaps': 'Aktuell',
  'monitoring.outdatedLaps': 'Veraltet',
  'monitoring.unanalyzedLaps': 'Nicht analysiert',
  'monitoring.shapeReady': '200 Segmente bereit',
  'monitoring.noShape': 'Keine Segmente generiert',
  'monitoring.reanalyzeTrack': 'Sync / Neu analysieren',
  'monitoring.forceReanalyzeTrack': 'Alle erzwingen',

  /* ── Terms & Conditions ────────────────────────────────────── */
  'terms.gateTitle': 'Allgemeine Geschäftsbedingungen',
  'terms.gateSubtitle': 'Bitte lies und akzeptiere die aktuellen Nutzungsbedingungen, um fortzufahren.',
  'terms.updatedBannerTitle': 'Aktualisierte Nutzungsbedingungen:',
  'terms.updatedBannerDesc': 'Unsere Nutzungsbedingungen wurden aktualisiert. Bitte lies und akzeptiere die neue Version, um fortzufahren.',
  'terms.checkboxLabel': 'Ich habe die Allgemeinen Geschäftsbedingungen gelesen und stimme ihnen zu',
  'terms.acceptButton': 'Akzeptieren & Weiter',
  'terms.acceptingButton': 'Wird gespeichert…',
  'terms.loadError': 'Die Nutzungsbedingungen konnten nicht geladen werden. Bitte überprüfe deine Verbindung und versuche es erneut.',
  'terms.acceptError': 'Deine Zustimmung konnte nicht gespeichert werden. Bitte versuche es erneut.',

  /* ── Search & Filter Bar ───────────────────────────────────── */
  'search.placeholder': 'Suchen oder Filter hinzufügen...',
  'search.button': 'Suchen',
  'search.syntaxHelp': 'Suchsyntax-Hilfe',
  'search.title': 'Suchsyntax-Anleitung',
  'search.subtitle': 'Filtern und suchen mit strukturierten Feldern und booleschen Operatoren.',
  'search.closeDialog': 'Schließen',
  'search.field.bareTextDesc': 'Suche über Standard-Textfelder',
  'search.operatorsTitle': 'Operatoren',
  'search.numericComparisons': 'Numerische / Datumsvergleiche',
  'search.range': 'Bereich (inklusive)',
  'search.rangeDesc': 'Werte zwischen Minimum und Maximum finden',
  'search.booleanLogic': 'Boolesche Logik',
  'search.booleanLogicDesc': 'Mehrere Bedingungen kombinieren',
  'search.booleanLogicUse': 'Verbinde Begriffe mit',
  'search.booleanLogicOr': 'oder',
  'search.booleanLogicGroup': 'und gruppiere Ausdrücke mit Klammern',
  'search.quotes': 'Anführungszeichen',
  'search.quotesDesc': 'Werte mit mehreren Wörtern in Anführungszeichen setzen (z.B. track:"South Garda")',
  'search.examplesTitle': 'Beispiele',

  'filter.operator.eq': 'ist',
  'filter.operator.neq': 'ist nicht',
  'filter.operator.gt': 'größer als',
  'filter.operator.gte': 'mindestens',
  'filter.operator.lt': 'kleiner als',
  'filter.operator.lte': 'höchstens',
  'filter.operator.range': 'zwischen',
  'filter.operator.contains': 'enthält',
  'filter.remove': 'Filter entfernen',
  'filter.done': 'Fertig',
  'filter.clearInput': 'Eingabe löschen',
  'filter.selectField': 'Nach Feld filtern',
  'filter.pressEnter': 'Eingabetaste zum Anwenden drücken',
  'filter.selectValues': 'Werte auswählen',
  'filter.noMatchingOptions': 'Keine passenden Optionen',

  'filter.bugs.bareSearchExample': 'Zusammenfassung, Beschreibung oder Melder durchsuchen',
  'filter.bugs.searchSummary': 'Nach Fehler-Zusammenfassung filtern',
  'filter.bugs.searchDescription': 'Nach Fehler-Beschreibung filtern',
  'filter.bugs.searchReporter': 'Nach Name oder E-Mail des Melders filtern',
  'filter.bugs.filterStatus': 'Nach Fehlerstatus filtern',
  'filter.bugs.filterSeverity': 'Nach Schweregrad filtern',
  'filter.bugs.filterPlatform': 'Nach Plattform filtern (web, ios, android)',
  'filter.bugs.filterDate': 'Nach Meldedatum filtern',
  'filter.bugs.exampleOpenHigh': 'Offene Fehler mit hohem Schweregrad',
  'filter.bugs.exampleMobileLogin': 'Mobile Login-Probleme',

  'filter.users.bareSearchExample': 'Name, E-Mail oder Benutzer-ID durchsuchen',
  'filter.users.searchName': 'Nach Anzeigename filtern',
  'filter.users.searchEmail': 'Nach E-Mail-Adresse filtern',
  'filter.users.searchId': 'Nach Benutzer-ID filtern',
  'filter.users.filterRole': 'Nach zugewiesener Rolle filtern',
  'filter.users.exampleAdmin': 'Benutzer mit Admin-Rolle',
  'filter.users.exampleMultiRole': 'Admins oder Editoren mit passendem Namen',

  'filter.races.bareSearchExample': 'Saison, Strecke, Event oder Fahrer durchsuchen',
  'filter.races.searchSeason': 'Nach Meisterschaftssaison filtern',
  'filter.races.filterYear': 'Nach Meisterschaftsjahr filtern',
  'filter.races.searchCountry': 'Nach Ländercode filtern',
  'filter.races.searchCategory': 'Nach Rennkategorie filtern',
  'filter.races.searchTrack': 'Nach Streckenname filtern',
  'filter.races.searchEvent': 'Nach Eventname filtern',
  'filter.races.filterRound': 'Nach Rundennummer filtern',
  'filter.races.searchSession': 'Nach Sessionname filtern',
  'filter.races.filterType': 'Nach Sessiontyp filtern',
  'filter.races.searchDriver': 'Nach Fahrername filtern',
  'filter.races.typePractice': 'Training',
  'filter.races.typeQualifying': 'Zeitfahren',
  'filter.races.typeHeat': 'Vorlauf',
  'filter.races.typePrefinal': 'Prefinale',
  'filter.races.typeFinal': 'Finale',
  'filter.races.exampleRokTrack': 'ROK-Cup-Events in South Garda',
  'filter.races.exampleFinalDriver': 'Finalläufe mit einem bestimmten Fahrer',

  'races.season': 'Saison',
  'races.year': 'Jahr',
  'races.country': 'Land',
  'races.event': 'Event',
  'races.roundLabel': 'Runde',
  'races.session': 'Session',
  'races.type': 'Typ',
  'races.label': 'Bezeichnung',
  'races.time': 'Zeit',

  'trackStudio.zoomIn': 'Vergrößern',
  'trackStudio.zoomOut': 'Verkleinern',
  'trackStudio.resetView': 'Ansicht zurücksetzen',
  'trackStudio.geoLocation': 'Geoposition',
  'trackStudio.finishLineTitle': 'Start- / Ziellinie',
  'trackStudio.finishLineHint': 'Ziehe die Flagge entlang der Strecke auf der Karte, klicke auf die Strecke oder nutze den Schieberegler unten.',
  'trackStudio.staticGpsPosition': 'Statische GPS-Position',
  'trackStudio.distance': 'Distanz',
  'trackStudio.previous': 'Zurück',
  'trackStudio.next': 'Weiter',

  'users.terms': 'AGB',
  'users.termsAccepted': 'Akzeptiert',
  'users.termsOutdated': 'Veraltet',
  'users.termsPending': 'Ausstehend',
  'users.resetTerms': 'Nutzungsbedingungen zurücksetzen',
  'users.confirmResetTerms': 'Zustimmung zu den Nutzungsbedingungen für {name} zurücksetzen? Die Nutzungsbedingungen müssen vor der weiteren Nutzung erneut geprüft und akzeptiert werden.',
  'users.termsReset': 'Zustimmung zu den Nutzungsbedingungen für {name} zurückgesetzt',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Nach {label} filtern',
  'filter.removeFilter': 'Filter {label} entfernen',
  'filter.prevMonth': 'Vorheriger Monat',
  'filter.nextMonth': 'Nächster Monat',
  'filter.selectStartDate': 'Startdatum im Kalender auswählen',
  'filter.op.exact': 'Genau',
  'filter.op.atLeast': 'Mindestens',
  'filter.op.atMost': 'Höchstens',
  'filter.op.between': 'Zwischen',
  'filter.op.greater': 'Größer',
  'filter.op.less': 'Kleiner',
  'filter.preset.today': 'Heute',
  'filter.preset.last7d': 'Letzte 7 Tage',
  'filter.preset.last30d': 'Letzte 30 Tage',
  'filter.preset.last90d': 'Letzte 90 Tage',
  'filter.preset.last1y': 'Letztes Jahr',
  'filter.preset.last7dShort': 'Letzte 7T',
  'filter.preset.last30dShort': 'Letzte 30T',
  'filter.preset.last90dShort': 'Letzte 90T',
  'filter.preset.last1yShort': 'Letztes 1J',
  'filter.sessionType.race': 'Rennen',
  'filter.sessionType.final': 'Finale',
  'filter.sessionType.pre_final': 'Prefinale',
  'filter.sessionType.heat': 'Vorlauf',
  'filter.sessionType.qualifying': 'Zeitfahren',
  'filter.unit.laps': 'Runden',
  'filter.field.date': 'Datum',
  'filter.field.track': 'Strecke',
  'filter.desc.track': 'Stimmt mit dem Streckennamen überein',
  'filter.field.championship': 'Meisterschaft',
  'filter.desc.championship': 'Name der Meisterschaftsserie',
  'filter.field.category': 'Kategorie',
  'filter.desc.category': 'Kanonische Rennkategorie',
  'filter.field.sessionType': 'Sessiontyp',
  'filter.desc.sessionType': 'Sessionklassifizierung (Rennen, Vorlauf, Zeitfahren, Finale)',
  'filter.field.laps': 'Runden',
  'filter.field.season': 'Saison',
  'filter.desc.season': 'Stimmt mit dem Saisonjahr überein',
  'filter.field.driver': 'Fahrer',
  'filter.placeholder.driver': 'z. B. Yanick',
  'filter.placeholder.email': 'z. B. gmail.com',
  'filter.desc.email': 'Stimmt mit der E-Mail-Adresse überein',
  'bugs.severity': 'Schweregrad',
  'bugs.severityCritical': 'Kritisch',
  'bugs.severityHigh': 'Hoch',
  'bugs.severityMedium': 'Mittel',
  'bugs.severityLow': 'Niedrig',
  'bugs.statusInProgress': 'In Bearbeitung',
  'bugs.statusClosed': 'Geschlossen',
  'bugs.submitter': 'E-Mail des Melders',
  'filter.bugs.title': 'Fehlerbericht-Suchsyntax',
  'filter.bugs.subtitle': 'Gemeldete Fehler nach Status, Schweregrad, E-Mail oder Stichwörtern filtern.',
  'filter.field.bugTitle': 'Zusammenfassung / Titel',
  'filter.placeholder.bugTitle': 'z. B. Absturz, Upload',
  'filter.desc.bugTitle': 'Stimmt mit Titel oder Beschreibung des Fehlers überein',
  'filter.desc.bugStatus': 'Bearbeitungsstatus (offen, in Bearbeitung, geschlossen)',
  'filter.desc.bugSeverity': 'Schweregrad des Fehlers',
  'filter.placeholder.bugEmail': 'z. B. nutzer@beispiel.de',
  'filter.desc.bugEmail': 'Stimmt mit der E-Mail des Melders überein',
  'filter.field.bugEnv': 'Umgebung',
  'filter.desc.bugEnv': 'Nach Meldeumgebung filtern (prod, dev, local)',
  'filter.bugs.example1': 'Alle offenen Fehlerberichte',
  'filter.bugs.example2': 'Offene Probleme in der Produktionsumgebung',
  'filter.bugs.example3': 'Offene Absturzberichte',
  'filter.field.driverEntries': 'Teilnehmerzahl',
  'filter.desc.driverEntries': 'Gesamtzahl der teilnehmenden Fahrer im Rennen',
  'filter.preset.entriesSmall': 'Klein (< 10)',
  'filter.preset.entriesMedium': 'Mittel (10..25)',
  'filter.preset.entriesLarge': 'Groß (> 25)',
  'filter.desc.raceLaps': 'Anzahl der absolvierten Rennrunden',
  'filter.desc.raceDriver': 'Stimmt mit dem Fahrernamen in den Rennergebnissen überein',
  'filter.desc.raceDate': 'Rennen nach Zeitraum oder Kalenderdatum filtern',
  'filter.races.example1': 'Rennen 2026 in Wohlen',
  'filter.races.example2': 'Große Starterfelder in Senior MAX',
  'filter.races.example3': 'Rennen mit Yanick im letzten Jahr',
  'filter.users.title': 'Benutzer-Suchsyntax',
  'filter.users.subtitle': 'Registrierte Benutzer nach Name, E-Mail, Rolle oder Anmeldeanbieter filtern.',
  'users.colName': 'Name',
  'users.colEmail': 'E-Mail',
  'users.colRole': 'Rolle',
  'users.colProvider': 'Anmeldeanbieter',
  'users.roleAdmin': 'Admin',
  'users.roleOwner': 'Eigentümer',
  'users.roleUser': 'Benutzer',
  'filter.desc.userName': 'Stimmt mit dem Anzeigenamen des Benutzers überein',
  'filter.desc.userRole': 'Benutzerrolle (Admin, Eigentümer, Benutzer)',
  'filter.desc.authProvider': 'Authentifizierungsanbieter',
  'filter.provider.password': 'Passwort',
  'filter.desc.userDate': 'Erstellungs- oder letztes Aktivitätsdatum',
  'filter.users.example1': 'Benutzer passend zu Yanick finden',
  'filter.users.example2': 'Plattform-Administratoren',
  'filter.users.example3': 'Google-Anmeldungen der letzten 30 Tage',
};

export default de;

